import { useBoolean } from "@chakra-ui/react";
import { useContextPreviewBlackboard } from "components/containers/inspectionDetail/context/PreviewBlackboard";
import { DroppableBox } from "components/containers/InspectionFormat/containers/LayoutFrameBuilder";
import { SOURCE } from "components/containers/InspectionFormat/Content/SettingPinLayoutFrame/context/pinLayoutFrameReducer";
import {
  PhotoBlackboardIcon,
  PhotoCameraBlackboardIcon,
} from "components/icon";
import { FileModel } from "interfaces/models";
import {
  BlackboardData,
  BlackboardPosition,
} from "interfaces/models/blackboard";
import { FileHistory } from "interfaces/models/pin";
import { lazy, memo, Suspense, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import ImageForm from "../../image/previewer/ImageForm";
import { FieldPreviewProps } from "../../types";
import ImageContainer from "./ImageContainer";
import useFieldImage, { ModalType } from "../../image/useFieldImage";

const DrawToolModalContainer = lazy(
  () => import("../../../components/DrawToolModalContainer")
);

const SelectFromGallery = lazy(
  () => import("../../../components/SelectFromGallery")
);

const CameraModal = lazy(() => import("../../../components/CameraModal"));
const FieldPreviewWrapper = lazy(
  () => import("../../../components/FieldPreviewWrapper")
);

const RndBlackboard = lazy(() => import("./RndBlackboard"));

export interface ImageHasBlackboardValue extends Omit<FileModel, "blackBoard"> {
  histories?: FileHistory[];
  blackboardData?: BlackboardData;
}

interface ImageHasBlackboardPreviewerProps
  extends FieldPreviewProps<ImageHasBlackboardValue> {}

const ImageHasBlackboardPreviewer: React.FC<
  ImageHasBlackboardPreviewerProps
> = (props) => {
  const {
    item,
    isLoading,
    isDraggable,
    index,
    allItemResultCount,
    prevItemResultCount,
    isDisabled,
  } = props;

  const {
    imageProps,
    modal,
    setModal,
    image,
    imageCaptured,
    disabled,
    isOpenImageForm,
    onOpenPhotoHistories,
    onOpenDrawToolModal,
    onDeleteImageHistory,
    onSelectFromGallery,
    onDraw,
    onCamera,
    pinSelected,
  } = useFieldImage({ ...props, isBlackboard: true });
  const [isResetDefaultPosition, resetDefaultPosition] = useBoolean();

  const { pinSelected: pinDetail } = useSelector(
    (state: RootState) => state.pin
  );
  const [isLoadedCanvas, loadedCanvas] = useBoolean();
  const isOpenRndBlackboard = modal === ModalType.DRAW_IMAGE && isLoadedCanvas;
  const blackboardPosition = image?.blackboardData;
  const blackboardPositionRef = useRef<BlackboardPosition | undefined>(
    blackboardPosition
  );
  if (blackboardPosition) {
    blackboardPositionRef.current = blackboardPosition;
  }

  const { blackboardFormat } = useContextPreviewBlackboard();

  const imageWithBlackboardProps = useMemo(
    () => ({ ...imageProps, pinDetail }),
    [imageProps, pinDetail]
  );

  const updateBlackboardPosition = useCallback((pos: BlackboardPosition) => {
    blackboardPositionRef.current = pos;
  }, []);

  const onResetDefaultPosition = useCallback(() => {
    resetDefaultPosition.on();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectFileByImageAction = useCallback(
    (file?: File) => {
      onResetDefaultPosition();
      onOpenDrawToolModal(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onOpenDrawToolModal]
  );

  const onOpenCameraByImageAction = useCallback(() => {
    onResetDefaultPosition();
    onCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCamera]);

  const onCloseDrawToolModal = useCallback(() => {
    setModal(null);
    loadedCanvas.off();
    resetDefaultPosition.off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isDraggable) {
    const component = (
      <ImageForm
        isDisabled={disabled}
        cameraIcon={
          <PhotoCameraBlackboardIcon width="2.4rem" height="2.4rem" />
        }
        imageIcon={<PhotoBlackboardIcon width="2.4rem" height="2.4rem" />}
        onOpenCamera={onCamera}
        onSelectFile={onOpenDrawToolModal}
      />
    );

    const cursor = isDisabled ? "not-allowed" : "pointer";

    return (
      <FieldPreviewWrapper
        index={index}
        allItemResultCount={allItemResultCount}
        prevItemResultCount={prevItemResultCount}
        title={item.name}
        data-type={item.dataType}
        sx={{
          "button, label": {
            cursor,
          },
          "&>div>div": {
            cursor,
            backgroundColor: isDisabled ? "#f2f2f2" : "#fff",
          },
        }}
      >
        {isDisabled ? (
          component
        ) : (
          <DroppableBox
            flex={1}
            name={item.name}
            icon={<PhotoBlackboardIcon />}
            id={item.id}
            source={SOURCE.INSPECTION_FORM}
          >
            {component}
          </DroppableBox>
        )}
      </FieldPreviewWrapper>
    );
  }

  return (
    <FieldPreviewWrapper
      title={item.name}
      data-type={item.dataType}
      data-value={image?.src}
      index={index}
      allItemResultCount={allItemResultCount}
      prevItemResultCount={prevItemResultCount}
    >
      {isOpenImageForm && (
        <ImageForm
          isDisabled={disabled}
          cameraIcon={
            <PhotoCameraBlackboardIcon width="2.4rem" height="2.4rem" />
          }
          imageIcon={<PhotoBlackboardIcon width="2.4rem" height="2.4rem" />}
          onOpenCamera={onCamera}
          onSelectFile={onOpenDrawToolModal}
        />
      )}

      {image?.src && (
        <ImageContainer
          isLoading={isLoading}
          isDisabled={disabled}
          imageWithBlackboardProps={imageWithBlackboardProps}
          onSelectFile={onSelectFileByImageAction}
          onOpenCamera={onOpenCameraByImageAction}
          onOpenPhotoHistories={onOpenPhotoHistories}
        />
      )}

      {modal === ModalType.CAMERA && (
        <CameraModal
          isOpen={true}
          onClose={() => setModal(null)}
          onCapture={onOpenDrawToolModal}
        />
      )}

      {isOpenRndBlackboard && (
        <Suspense>
          <RndBlackboard
            isOpen={isOpenRndBlackboard}
            isLoadedImage={isLoadedCanvas}
            imageSrc={image?.src}
            blackboardData={image?.blackboardData}
            layoutUrl={blackboardFormat?.layoutUrl}
            pinDetail={pinDetail}
            originWidth={blackboardFormat?.width}
            originHeight={blackboardFormat?.height}
            isResetDefaultPosition={isResetDefaultPosition}
            updateBlackboardPosition={updateBlackboardPosition}
          />
        </Suspense>
      )}

      {pinSelected && (
        <>
          <DrawToolModalContainer
            isOpen={modal === ModalType.DRAW_IMAGE}
            image={image}
            setImage={(image, isAddHistory) =>
              onDraw(image, isAddHistory, {
                blackboardData: blackboardPositionRef.current,
              })
            }
            imageCaptured={imageCaptured}
            onLoadedCanvas={loadedCanvas.on}
            onClose={onCloseDrawToolModal}
            onResetDefaultPosition={onResetDefaultPosition}
            pinId={pinSelected.id}
            itemResultId={item.id}
          />

          {modal === ModalType.GALLERY && (
            <SelectFromGallery
              isOpen={true}
              value={image?.src}
              pinId={pinSelected.id}
              itemResultId={item.id}
              imageWithBlackboardProps={imageWithBlackboardProps}
              onDeleteImage={onDeleteImageHistory}
              onChange={onSelectFromGallery}
              onClose={() => setModal(null)}
            />
          )}
        </>
      )}
    </FieldPreviewWrapper>
  );
};

export default memo(ImageHasBlackboardPreviewer);
